// src/AdminDashboard/components/MultiStepGameForm/MultiStepGameForm.js
import React, { useState } from "react";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import axios from "axios";
import IntroStep from "./steps/IntroStep";
import BrandingStep from "./steps/BrandingStep";
import ContentStep from "./steps/ContentStep";
import MemoryContentStep from "./steps/MemoryContentStep";
import ColorsStep from "./steps/ColorsStep";
import SuccessStep from "./steps/SuccessStep";
import GamePreview from "./GamePreview/GamePreview";
import { getInitialFormState } from "./initialFormState";
import {
  GAME_TYPE_FORM_CONFIG,
  STEP_DEFINITIONS,
  getAllowedGameTypes,
} from "./config/gameTypeFormConfig";
import { DEFAULT_CONNECTIONS_FORM_FIELDS } from "./defaults/connectionsDefaults";
import { DEFAULT_MEMORY_FORM_FIELDS } from "./defaults/memoryDefaults";
import "./MultiStepGameForm.scss";

const STEP_COMPONENTS = {
  INTRO: IntroStep,
  BRANDING: BrandingStep,
  CONTENT: ContentStep,
  COLORS: ColorsStep,
  MEMORY: MemoryContentStep,
  SUCCESS: SuccessStep,
};

const MultiStepGameForm = ({
  game,
  onSuccess,
  onCancel,
  getAuthHeaders,
  client,
  gameAccess = ["all"],
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(getInitialFormState(game));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const allowedTypes = getAllowedGameTypes(gameAccess);
  const selectedType = allowedTypes.includes(formData.type)
    ? formData.type
    : "";
  const selectedTypeConfig = selectedType
    ? GAME_TYPE_FORM_CONFIG[selectedType]
    : null;
  const stepIds = selectedTypeConfig ? selectedTypeConfig.steps : ["INTRO"];
  const hasStep = (name) => stepIds.includes(name);
  const steps = stepIds.map((stepName, index) => ({
    id: STEP_DEFINITIONS[stepName]?.id || index + 1,
    name: stepName,
    component: STEP_COMPONENTS[stepName],
  }));
  const currentStepIndex = Math.min(currentStep, steps.length) - 1;

  const updateFormData = (field, value) => {
    if (field === "type") {
      const nextType = allowedTypes.includes(value) ? value : "";
      const typeDefaults =
        nextType === "memory"
          ? DEFAULT_MEMORY_FORM_FIELDS
          : DEFAULT_CONNECTIONS_FORM_FIELDS;

      setFormData((prev) => ({
        ...prev,
        type: nextType,
        name:
          nextType && !(prev.name || "").trim()
            ? typeDefaults.introMessage.title
            : prev.name,
        ...(nextType
          ? {
              introMessage: typeDefaults.introMessage,
              terms: typeDefaults.terms,
              termsType: typeDefaults.termsType,
              makeGradient: typeDefaults.makeGradient,
              backgroundColor: typeDefaults.backgroundColor,
              backgroundImage: typeDefaults.backgroundImage,
              bannerImage: typeDefaults.bannerImage,
              textColor: typeDefaults.textColor,
              buttonColor: typeDefaults.buttonColor,
              gameData: typeDefaults.gameData,
              categoryColors: typeDefaults.categoryColors,
              cardBackgroundColor: typeDefaults.cardBackgroundColor,
              cardTextColor: typeDefaults.cardTextColor,
              categoryTextColor: typeDefaults.categoryTextColor,
              successMessage: typeDefaults.successMessage,
              redirectButtonText: typeDefaults.redirectButtonText,
              redirectButtonLink: typeDefaults.redirectButtonLink,
              redirectButtonColor: typeDefaults.redirectButtonColor,
              reveal: typeDefaults.reveal,
            }
          : {}),
      }));
      setCurrentStep(1);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field errors when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateCurrentStep = () => {
    const errors = {};
    const currentStepName = steps[currentStepIndex]?.name;

    switch (currentStepName) {
      case "INTRO":
        if (!formData.name?.trim()) {
          errors.name = "Game name is required";
        }
        if (selectedType && !formData.introMessage?.title?.trim()) {
          errors.title = "Display title is required";
        }
        if (formData.termsType === "url") {
          const url = (formData.terms || "").trim();
          if (!url) {
            errors.terms = "Terms URL is required when URL is selected";
          } else if (!/^https?:\/\//i.test(url)) {
            errors.terms = "Terms URL must start with http:// or https://";
          }
        }
        // Scheduling validation: prevent past times and ensure valid range
        const now = new Date();
        if (formData.activeFrom) {
          const start = new Date(formData.activeFrom);
          if (isNaN(start.getTime())) {
            errors.activeWindow = "Invalid start date/time";
          } else if (start < now) {
            errors.activeWindow = "Start time cannot be in the past";
          }
        }
        if (formData.activeUntil) {
          const end = new Date(formData.activeUntil);
          if (isNaN(end.getTime())) {
            errors.activeWindow = "Invalid end date/time";
          } else if (end < now) {
            errors.activeWindow = "End time cannot be in the past";
          }
        }
        if (formData.activeFrom && formData.activeUntil) {
          const start = new Date(formData.activeFrom);
          const end = new Date(formData.activeUntil);
          if (
            !isNaN(start.getTime()) &&
            !isNaN(end.getTime()) &&
            end <= start
          ) {
            errors.activeWindow = "End time must be after start time";
          }
        }
        if (!formData.type || !allowedTypes.includes(formData.type)) {
          errors.type = "Select an available game type";
        }
        break;

      case "BRANDING":
        if (client === "delta") {
          const folder = formData?.reveal?.logo?.folder || "";
          const image = formData?.reveal?.logo?.image || "";
          if (folder && !image) {
            errors.logo =
              "Please select a Delta logo image for the chosen family";
          }
        }
        break;

      case "CONTENT":
        if (!hasStep("CONTENT")) break;
        if (Object.keys(formData.gameData).length !== 4) {
          errors.categories = "You must have exactly 4 categories";
        }
        const invalidCategories = Object.entries(formData.gameData).filter(
          ([category, words]) =>
            !words || words.length !== 4 || words.some((word) => !word?.trim())
        );
        if (invalidCategories.length > 0) {
          errors.words = `Each category must have exactly 4 words. Check: ${invalidCategories
            .map(([cat]) => cat)
            .join(", ")}`;
        }
        break;

      case "COLORS":
        if (!hasStep("COLORS")) break;
        if (!formData.categoryColors || formData.categoryColors.length !== 4) {
          errors.colors = "You must select 4 category colors";
        }
        break;

      case "MEMORY": {
        if (!hasStep("MEMORY")) break;
        const pairs = Array.isArray(formData?.gameData?.pairs)
          ? formData.gameData.pairs
          : [];
        const settings =
          formData?.gameData?.settings &&
          typeof formData.gameData.settings === "object"
            ? formData.gameData.settings
            : {};
        const pairCount = Number(settings.pairCount || pairs.length);
        if (!Number.isInteger(pairCount) || pairCount < 1 || pairCount > 14) {
          errors.memoryPairCount =
            "Memory game pair count must be between 1 and 14";
        }
        if (pairs.length !== pairCount) {
          errors.memoryPairs = "Pair count must match the number of image URLs";
        }
        if (
          pairs.some(
            (pair) =>
              !/^https?:\/\/.+/i.test(String(pair?.imageUrl || "").trim())
          )
        ) {
          errors.memoryPairs = "Every pair must include a valid image URL";
        }
        if (
          !/^https?:\/\/.+/i.test(
            String(settings.cardBackImageUrl || "").trim()
          )
        ) {
          errors.memoryBackImage = "Card back image must be a valid URL";
        }
        if (
          settings.timerSeconds !== null &&
          settings.timerSeconds !== undefined &&
          (!Number.isInteger(Number(settings.timerSeconds)) ||
            Number(settings.timerSeconds) < 0)
        ) {
          errors.memoryTimer =
            "Timer must be empty or a whole number of seconds";
        }
        break;
      }

      case "SUCCESS":
        if (!formData.successMessage?.trim()) {
          errors.successMessage = "Success message is required";
        }
        break;

      default:
        break;
    }

    setFieldErrors(errors);
    return errors;
  };

  const handleNext = () => {
    const errors = validateCurrentStep();
    if (Object.keys(errors).length === 0) {
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
      setError("");
      setFieldErrors({});
    } else {
      const messages = Object.values(errors).filter(Boolean);
      setError(
        messages.length
          ? `Please fix the following issues:\n• ${messages.join("\n• ")}`
          : ""
      );
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError("");
      setFieldErrors({});
    }
  };

  const handleSubmit = async () => {
    const errors = validateCurrentStep();
    if (Object.keys(errors).length > 0) {
      const messages = Object.values(errors).filter(Boolean);
      setError(
        messages.length
          ? `Please fix the following issues:\n• ${messages.join("\n• ")}`
          : ""
      );
      return;
    }

    setLoading(true);
    setError("");
    setFieldErrors({});

    try {
      const { termsType, makeGradient, ...rest } = formData;
      const normalizedTerms = (() => {
        if (termsType === "none") return "";
        if (termsType === "url") {
          const raw = (formData.terms || "").trim();
          if (!raw) return "";
          return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        }
        return formData.terms || ""; // text (rich HTML)
      })();

      const payload = {
        ...rest,
        reveal: {
          ...(rest.reveal || {}),
          makeGradient: Boolean(makeGradient),
        },
        terms: normalizedTerms,
        // Ensure client is set if not already in formData
        client: formData.client || client,
        // Ensure admin notes are sent explicitly
        adminNotes: formData.adminNotes || "",
        // Normalize schedule values to ISO strings with timezone
        activeFrom: formData.activeFrom
          ? new Date(formData.activeFrom).toISOString()
          : null,
        activeUntil: formData.activeUntil
          ? new Date(formData.activeUntil).toISOString()
          : null,
      };

      if (game) {
        // Update existing game
        await axios.put(
          "/api/games",
          { id: game.id, ...payload },
          getAuthHeaders()
        );
      } else {
        // Create new game
        await axios.post("/api/games", payload, getAuthHeaders());
      }

      onSuccess();
    } catch (error) {
      setError(error.response?.data?.error || "Failed to save game");
    } finally {
      setLoading(false);
    }
  };

  const CurrentStepComponent = steps[currentStepIndex]?.component || IntroStep;
  const hideGameSpecificIntroInputs = !selectedType;

  return (
    <div className="multi-step-game-form">
      <div className="form-layout">
        {/* Left Side - Form */}
        <div className="form-side">
          <div className="breadcrumb">
            <span className="breadcrumb-item clickable" onClick={onCancel}>
              DASHBOARD
            </span>
            {steps.slice(0, currentStep).map((step, index) => (
              <React.Fragment key={step.id}>
                <span className="breadcrumb-separator"> / </span>
                <span
                  className={`breadcrumb-item ${
                    index === currentStepIndex ? "active" : ""
                  }`}
                >
                  {step.name === "INTRO" ? "INTRO & BRANDING" : step.name}
                </span>
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div className="error-message" style={{ whiteSpace: "pre-line" }}>
              {error}
            </div>
          )}

          <CurrentStepComponent
            formData={formData}
            updateFormData={updateFormData}
            fieldErrors={fieldErrors}
            client={client}
            getAuthHeaders={getAuthHeaders}
            gameAccess={gameAccess}
            hideTypeSelector={false}
            hideGameSpecificInputs={
              CurrentStepComponent === IntroStep && hideGameSpecificIntroInputs
            }
          />
        </div>

        {/* Right Side - Preview */}
        <div className="preview-side">
          <div className="preview-header">
            <span>GAME PREVIEW</span>
          </div>

          <GamePreview
            currentStep={currentStep}
            formData={formData}
            client={client}
          />
          {hideGameSpecificIntroInputs ? null : (
            <div className="form-actions">
              {currentStep > 1 && (
                <button
                  className="btn btn-secondary back-step icon-only"
                  onClick={handleBack}
                  disabled={loading}
                  title="Back"
                  aria-label="Back"
                  type="button"
                >
                  <FiArrowLeft />
                </button>
              )}

              {currentStep < steps.length ? (
                <button
                  className="btn btn-primary next-step"
                  onClick={handleNext}
                  disabled={loading}
                >
                  NEXT STEP
                </button>
              ) : (
                <button
                  className="btn btn-primary save-finish"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "SAVING..." : "SAVE & FINISH"}
                </button>
              )}
              {currentStep < steps.length && (
                <button
                  className="btn btn-icon save-icon"
                  onClick={handleSubmit}
                  disabled={loading}
                  title="Save & Finish"
                  aria-label="Save & Finish"
                  type="button"
                >
                  <FiSave />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepGameForm;
